import { motion } from "motion/react";
import { MousePointer, ShoppingCart, Mail, CreditCard, CheckCircle, Eye, AlertCircle, TrendingUp } from "lucide-react";

export function TimelineSection() {
  const timelineEvents = [
    {
      icon: Eye,
      time: "14:23:45",
      title: "Primeira Visita",
      description: "Visitante chegou via Google Ads - Campanha 'Black Friday'",
      metadata: "IP: 187.45.xxx.xxx | São Paulo, SP",
      color: "#00F5FF",
      type: "detection"
    },
    {
      icon: MousePointer,
      time: "14:25:12",
      title: "Navegação Ativa",
      description: "Visitou 5 páginas de produto | Categoria: Eletrônicos",
      metadata: "Tempo médio: 2min 34s por página",
      color: "#00F5FF",
      type: "engagement"
    },
    {
      icon: ShoppingCart,
      time: "14:31:08",
      title: "Carrinho Criado",
      description: "Adicionou 3 itens | Valor total: R$ 2.847,00",
      metadata: "iPhone 15 Pro, AirPods Pro, Capa",
      color: "#10B981",
      type: "conversion"
    },
    {
      icon: Mail,
      time: "14:32:15",
      title: "Lead Capturado",
      description: "Email identificado: joao.silva@email.com",
      metadata: "Identity Score: 95% | Lead Qualificado",
      color: "#00F5FF",
      type: "identity"
    },
    {
      icon: AlertCircle,
      time: "14:35:42",
      title: "Abandono Detectado",
      description: "Saiu na página de checkout (etapa de pagamento)",
      metadata: "Trigger de recuperação ativado",
      color: "#FF4D00",
      type: "abandon"
    },
    {
      icon: Mail,
      time: "14:40:00",
      title: "Email Automático Enviado",
      description: "Sequência de recuperação iniciada",
      metadata: "Template: 'Carrinho Esperando Você' + Cupom 10%",
      color: "#00F5FF",
      type: "automation"
    },
    {
      icon: MousePointer,
      time: "18:15:23",
      title: "Retorno ao Site",
      description: "Visitante retornou via email (clique no CTA)",
      metadata: "Tempo de retorno: 3h 35min",
      color: "#00F5FF",
      type: "return"
    },
    {
      icon: CreditCard,
      time: "18:18:45",
      title: "Checkout Iniciado",
      description: "Aplicou cupom de desconto | Novo valor: R$ 2.562,30",
      metadata: "Método escolhido: Cartão de Crédito",
      color: "#10B981",
      type: "checkout"
    },
    {
      icon: CheckCircle,
      time: "18:20:12",
      title: "Compra Concluída! 🎉",
      description: "Conversão realizada | Receita: R$ 2.562,30",
      metadata: "Recuperado com sucesso | ROI: +256%",
      color: "#10B981",
      type: "success"
    }
  ];

  const getIconColor = (color: string) => {
    return color;
  };

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14] via-[#0B0E14]/98 to-[#0B0E14]" />

      {/* Animated grid */}
      <div className="absolute inset-0 opacity-10">
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, #00F5FF 1px, transparent 1px),
              linear-gradient(to bottom, #00F5FF 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
          animate={{ backgroundPosition: ['0% 0%', '100% 100%'] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00F5FF]/30 bg-[#00F5FF]/5 mb-6">
            <TrendingUp className="w-4 h-4 text-[#00F5FF]" />
            <span className="text-sm text-[#00F5FF] font-mono">LEAD LIFELINE V32</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Jornada Completa do
            <span className="block text-[#00F5FF]">Visitante ao Cliente</span>
          </h2>

          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Cada ação, cada clique, cada hesitação. Rastreamos a jornada completa e ativamos 
            automações inteligentes para <span className="text-[#10B981] font-bold">recuperar vendas perdidas</span>
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="max-w-5xl mx-auto relative">
          {/* Vertical line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00F5FF]/50 via-[#00F5FF]/30 to-[#10B981]/50" />

          {/* Timeline events */}
          <div className="space-y-12">
            {timelineEvents.map((event, idx) => {
              const Icon = event.icon;
              const isLeft = idx % 2 === 0;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className={`relative flex items-center ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  } flex-col md:gap-8 gap-4`}
                >
                  {/* Content Card */}
                  <div className={`flex-1 ${isLeft ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'} w-full md:w-auto ml-12 md:ml-0`}>
                    <motion.div
                      whileHover={{ scale: 1.02, y: -5 }}
                      className="relative group"
                    >
                      {/* Glow effect */}
                      <div
                        className="absolute -inset-0.5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-lg"
                        style={{ backgroundColor: event.color }}
                      />

                      {/* Card */}
                      <div
                        className="relative p-6 rounded-xl border backdrop-blur-sm bg-gradient-to-br from-[#0B0E14]/90 to-[#0B0E14]/50"
                        style={{ borderColor: `${event.color}30` }}
                      >
                        {/* Time badge */}
                        <div
                          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono mb-3"
                          style={{
                            backgroundColor: `${event.color}15`,
                            color: event.color,
                            border: `1px solid ${event.color}40`
                          }}
                        >
                          {event.time}
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-bold text-white mb-2">
                          {event.title}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-300 mb-2">
                          {event.description}
                        </p>

                        {/* Metadata */}
                        <p className="text-sm text-gray-400 font-mono">
                          {event.metadata}
                        </p>

                        {/* Scan line effect */}
                        <motion.div
                          className="absolute inset-0 pointer-events-none rounded-xl overflow-hidden"
                          initial={{ opacity: 0 }}
                          whileHover={{ opacity: 1 }}
                        >
                          <motion.div
                            className="absolute inset-x-0 h-px"
                            style={{ backgroundColor: event.color, opacity: 0.3 }}
                            animate={{ y: [0, 200] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                          />
                        </motion.div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Icon in center */}
                  <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 z-10">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: idx * 0.1 }}
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      className="w-12 h-12 rounded-full flex items-center justify-center border-4 border-[#0B0E14] shadow-lg"
                      style={{
                        backgroundColor: `${event.color}20`,
                        boxShadow: `0 0 20px ${event.color}40`
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: getIconColor(event.color) }} />
                    </motion.div>
                  </div>

                  {/* Spacer for opposite side */}
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20 grid md:grid-cols-4 gap-6"
        >
          {[
            { label: 'Tempo Total', value: '3h 56min', color: '#00F5FF' },
            { label: 'Touch Points', value: '9 eventos', color: '#00F5FF' },
            { label: 'Taxa de Recuperação', value: '100%', color: '#10B981' },
            { label: 'ROI da Automação', value: '+256%', color: '#10B981' }
          ].map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.5 + idx * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="p-6 rounded-xl border bg-gradient-to-br from-[#0B0E14] to-[#0B0E14]/50 backdrop-blur-sm text-center"
              style={{ borderColor: `${stat.color}30` }}
            >
              <div className="text-3xl font-bold font-mono mb-2" style={{ color: stat.color }}>
                {stat.value}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-xl text-gray-300 mb-8">
            Isso não é ficção científica. É o que acontece em <span className="text-[#00F5FF] font-bold">tempo real</span> nas 
            lojas que usam WPTT.
          </p>
          <button className="px-10 py-5 bg-[#FF4D00] hover:bg-[#FF4D00]/90 text-white text-lg rounded-lg font-bold transition-all hover:scale-105 hover:shadow-lg hover:shadow-[#FF4D00]/50">
            Quero Rastrear Assim Também
          </button>
        </motion.div>
      </div>
    </section>
  );
}
